import React, { useContext, useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { AppHeader } from "../AppHeader";
import { LocationContext } from "../../contexts/Location";
import { TrailList } from "../trail/TrailList";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "../ui/sheet";

interface MainLayoutProps {
  children?: React.ReactNode;
}

const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  const { trails, homepageOverview, activitiesLoaded, activitiesLoading, activitiesError, setTriggerGetTrails } = useContext(LocationContext);
  const location = useLocation();
  const navigate = useNavigate();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isMobileNavigationOpen, setIsMobileNavigationOpen] = useState(false);
  const { trailId } = useParams(); // Get trailId for sidebar selection

  useEffect(() => {
    setIsMobileNavigationOpen(false);
  }, [location.pathname]);

  // Determine selectedTrailId for TrailList from URL
  const selectedTrailIdFromUrl = trailId ?? null; // Ensure it's string or null

  // Determine if a sidebar should be rendered based on the current route
  const shouldRenderSidebar = location.pathname.startsWith('/trails');

  return (
    <div className="flex h-[100dvh] flex-col overflow-hidden">
      <AppHeader
        trailCount={homepageOverview?.totalActivityCount ?? 0}
        onOpenTrailNavigation={shouldRenderSidebar ? () => setIsMobileNavigationOpen(true) : undefined}
      />
      <div className="flex min-h-0 min-w-0 flex-1 overflow-hidden">
        {shouldRenderSidebar && (
          <div
            className={`trail-desktop-sidebar flex-shrink-0 border-r border-border shadow-lg transition-all duration-300 ${
              isSidebarCollapsed ? "w-16" : "w-80"
            }`}
          >
            {activitiesError ? (
              <div className="p-4 text-sm" role="alert">
                <p>Impossible de charger les pistes.</p>
                <button className="mt-2 underline" onClick={() => setTriggerGetTrails(true)}>Réessayer</button>
              </div>
            ) : activitiesLoaded ? (
              <TrailList
                trails={trails}
                selectedTrailId={selectedTrailIdFromUrl}
                onSelectTrail={(id) => navigate(`/trails/${id}`)}
                onCreateNew={() => navigate('/trails/new')}
                isCollapsed={isSidebarCollapsed}
                onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
              />
            ) : (
              <div className="p-4 text-sm text-muted-foreground">{activitiesLoading ? "Chargement des pistes..." : "Préparation des pistes..."}</div>
            )}
          </div>
        )}
        <div className="min-h-0 min-w-0 flex-1 overflow-auto"> {/* Main content area */}
          {children || <Outlet />}
        </div>
      </div>
      {shouldRenderSidebar && (
        <Sheet open={isMobileNavigationOpen} onOpenChange={setIsMobileNavigationOpen}>
          <SheetContent
            side="left"
            className="trail-mobile-navigation-sheet gap-0 p-0"
            style={{
              top: 0,
              bottom: 0,
              left: 0,
              width: "min(20rem, 88vw)",
              maxWidth: "20rem",
              height: "100dvh",
            }}
          >
            <SheetHeader className="sr-only">
              <SheetTitle>Navigation des activités</SheetTitle>
              <SheetDescription>Liste et filtres des activités enregistrées.</SheetDescription>
            </SheetHeader>
            {activitiesError ? (
              <div className="p-4 text-sm" role="alert">
                <p>Impossible de charger les pistes.</p>
                <button className="mt-2 underline" onClick={() => setTriggerGetTrails(true)}>Réessayer</button>
              </div>
            ) : activitiesLoaded ? (
              <TrailList
                trails={trails}
                selectedTrailId={selectedTrailIdFromUrl}
                onSelectTrail={(id) => {
                  navigate(`/trails/${id}`);
                  setIsMobileNavigationOpen(false);
                }}
                onCreateNew={() => {
                  navigate('/trails/new');
                  setIsMobileNavigationOpen(false);
                }}
                isCollapsed={false}
                onToggleCollapse={() => setIsMobileNavigationOpen(false)}
              />
            ) : (
              <div className="p-4 text-sm text-muted-foreground">{activitiesLoading ? "Chargement des pistes..." : "Préparation des pistes..."}</div>
            )}
          </SheetContent>
        </Sheet>
      )}
    </div>
  );
};

export default MainLayout;